import {
  ElementNode,
  $createParagraphNode,
  $parseSerializedNode,
} from 'lexical';
import { getGlobalHeaderFooterContent, restoreGlobalContentToRegion } from '../../shared/globalHeaderFooterState';

export class PageHeaderNode extends ElementNode {
  static getType() {
    return 'page_header';
  }

  static clone(node) {
    return new PageHeaderNode(node.__key);
  }

  createDOM() {
    const el = document.createElement('div');
    el.setAttribute('data-page-header', 'true');
    el.className = 'lx-page-header';
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', 'Page Header');
    return el;
  }

  updateDOM() {
    return false;
  }

  static importJSON(serialized) {
    const node = new PageHeaderNode();
    node.setFormat(serialized.format);
    node.setIndent(serialized.indent);
    node.setDirection(serialized.direction);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'page_header',
      version: 1,
    };
  }
}

export class PageBodyNode extends ElementNode {
  static getType() {
    return 'page_body';
  }

  static clone(node) {
    return new PageBodyNode(node.__key);
  }

  createDOM() {
    const el = document.createElement('div');
    el.setAttribute('data-page-body', 'true');
    el.className = 'lx-page-body';
    el.setAttribute('role', 'main');
    el.setAttribute('aria-label', 'Page Content');
    return el;
  }

  updateDOM() {
    return false;
  }

  static importJSON(serialized) {
    const node = new PageBodyNode();
    node.setFormat(serialized.format);
    node.setIndent(serialized.indent);
    node.setDirection(serialized.direction);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'page_body',
      version: 1,
    };
  }
}

export class PageFooterNode extends ElementNode {
  static getType() {
    return 'page_footer';
  }

  static clone(node) {
    return new PageFooterNode(node.__key);
  }

  createDOM() {
    const el = document.createElement('div');
    el.setAttribute('data-page-footer', 'true');
    el.className = 'lx-page-footer';
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', 'Page Footer');
    return el;
  }

  updateDOM() {
    return false;
  }

  static importJSON(serialized) {
    const node = new PageFooterNode();
    node.setFormat(serialized.format);
    node.setIndent(serialized.indent);
    node.setDirection(serialized.direction);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'page_footer',
      version: 1,
    };
  }
}

export class PageNode extends ElementNode {
  static getType() {
    return 'page';
  }

  static clone(node) {
    return new PageNode(node.__key);
  }

  createDOM() {
    const el = document.createElement('section');
    el.className = 'lx-page';
    el.setAttribute('data-lx-page', 'true');
    return el;
  }

  updateDOM() {
    return false;
  }

  static importJSON(serialized) {
    const node = new PageNode();
    node.setFormat(serialized.format);
    node.setIndent(serialized.indent);
    node.setDirection(serialized.direction);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'page',
      version: 1,
    };
  }

  isShadowRoot() {
    return true;
  }

  getHeader() {
    const kids = this.getChildren();
    for (const k of kids) {
      if (typeof k?.getType === 'function' && k.getType() === 'page_header') return k;
    }
    return null;
  }

  getBody() {
    const kids = this.getChildren();
    for (const k of kids) {
      if (typeof k?.getType === 'function' && k.getType() === 'page_body') return k;
    }
    return null;
  }

  getFooter() {
    const kids = this.getChildren();
    for (const k of kids) {
      if (typeof k?.getType === 'function' && k.getType() === 'page_footer') return k;
    }
    return null;
  }
}

export function $createPageHeaderNode() {
  const node = new PageHeaderNode();
  node.append($createParagraphNode());
  return node;
}

export function $createPageBodyNode() {
  const node = new PageBodyNode();
  node.append($createParagraphNode());
  return node;
}

export function $createPageFooterNode() {
  const node = new PageFooterNode();
  node.append($createParagraphNode());
  return node;
}

export function $createPageNode() {
  const page = new PageNode();
  page.append($createPageHeaderNode());
  page.append($createPageBodyNode());
  page.append($createPageFooterNode());
  return page;
}

/**
 * Recursively serialize a node and all its children.
 * node.exportJSON() does NOT automatically include children - we must do it manually.
 */
function serializeNodeRecursively(node) {
  const json = node.exportJSON();
  
  // If the node has children (is an ElementNode), serialize them recursively
  if (typeof node.getChildren === 'function') {
    const children = node.getChildren();
    json.children = children.map(child => serializeNodeRecursively(child));
  }
  
  return json;
}

/**
 * Deep clones a node using recursive serialization and parseSerializedNode.
 */
function $cloneNode(node) {
  const serialized = serializeNodeRecursively(node);
  return $parseSerializedNode(serialized);
}

/**
 * Copies children from source region to destination region.
 */
function copyRegionChildren(srcRegion, dstRegion) {
  if (!srcRegion || !dstRegion) return;
  
  // Clear existing children in destination
  const dstChildren = dstRegion.getChildren();
  for (const child of dstChildren) {
    child.remove();
  }
  
  // Clone and append source children
  const srcChildren = srcRegion.getChildren();
  for (const child of srcChildren) {
    const cloned = $cloneNode(child);
    dstRegion.append(cloned);
  }
}

/**
 * Creates a new page with header/footer content copied from global state or source page.
 * This ensures new pages immediately have the same header/footer as existing pages.
 * @param {PageNode} sourcePage - Optional source page to copy header/footer from if global state is empty
 * @returns {PageNode} - The new page with copied header/footer
 */
export function $createPageNodeWithHeaderFooter(sourcePage) {
  const newPage = $createPageNode();
  const dstHeader = newPage.getHeader();
  const dstFooter = newPage.getFooter();
  
  // First try to use global header/footer content (most reliable)
  const globalContent = getGlobalHeaderFooterContent();
  
  if (globalContent.header && dstHeader) {
    restoreGlobalContentToRegion(dstHeader, globalContent.header);
  } else if (sourcePage && dstHeader) {
    // Fallback: copy from source page if global state not yet initialized
    const srcHeader = sourcePage.getHeader();
    if (srcHeader) {
      copyRegionChildren(srcHeader, dstHeader);
    }
  }
  
  if (globalContent.footer && dstFooter) {
    restoreGlobalContentToRegion(dstFooter, globalContent.footer);
  } else if (sourcePage && dstFooter) {
    // Fallback: copy from source page if global state not yet initialized
    const srcFooter = sourcePage.getFooter();
    if (srcFooter) {
      copyRegionChildren(srcFooter, dstFooter);
    }
  }
  
  return newPage;
}

export function $isPageNode(node) { 
  return node instanceof PageNode;
}

if (module.hot) {
  module.hot.decline();
}
